import React, { Suspense } from 'react'
import { useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import useMenuStore, { flattenMenuUrls } from '@/store/menuStore'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

const NotFoundPage = lazyWithRetry(() => import('@/pages/Error/NotFoundPage'))
const ForbiddenPage = lazyWithRetry(() => import('@/pages/Error/ForbiddenPage'))

/**
 * All routes that are always accessible regardless of menu permissions.
 * These are structural routes, not content pages.
 */
const PUBLIC_PATHS = new Set(['/dashboard'])

/**
 * All routes registered in the router (i.e. routes that actually exist).
 * A path not in this set → 404.
 * A path in this set but not in the menu → 403.
 */
export const REGISTERED_PATHS = new Set([
  '/dashboard',
  '/users',
  '/orders',
  '/system/menu',
  '/system/role',
  '/system/admin',
  '/trading/pairs',
  '/user/kyc',
  '/user/advanced-verification',
  '/user/list',
  '/user/registration-code',
  '/user/account-overview',
  '/config/announcement',
  '/config/activity-popups',
  '/wallet/accounts',
  '/wallet/ledger',
  '/wallet/deposits',
  '/wallet/withdrawals',
  '/wallet/deposit-addresses',
  '/wallet/platform-deposit-addresses',
  '/earn/products',
  '/earn/holdings',
  '/earn/orders',
  '/earn/redemptions',
  '/earn/rewards',
  '/trading/spot-orders',
  '/trading/contract-orders',
  '/trading/contract-positions',
  '/trading/contract-risk-snapshot',
  '/trading/contract-liquidations',
  '/trading/contract-funding-history',
  '/trading/binary-options-config',
  '/trading/binary-options-orders',
  '/trading/binary-options-user-overrides',
  '/system/settings',
  '/system/logs',
  '/system/messages',
  '/system/content-pages',
  '/system/home-layouts',
  '/system/surveys',
  '/news/news-sources',
  '/news/news-articles',
  '/news/news-categories',
  '/news/market-analyses',
  '/ai-quant/strategies',
  '/ai-quant/templates',
  '/ai-quant/trades',
  '/file/file-list',
  '/margin/configs',
  '/margin/loans',
  '/margin/contract-templates',
  '/margin/repayment-claims',
  '/margin/repayment-addresses',
  '/otc/orders',
  '/otc/payment-accounts',
  '/otc/quotes',
  '/quant-funds/products',
  '/quant-funds/orders',
  '/quant-funds/user-products',
  '/quant-funds/participation-limits',
  '/promotion/campaigns',
  '/promotion/assignments',
  '/referral/relations',
  '/copy-trade/traders',
  '/copy-trade/positions',
  '/copy-trade/subscriptions',
  '/copy-trade/execution-logs',
  '/wallet/convert-orders',
  '/wallet/convert-exposure',
  '/customer-service/workbench',
  '/customer-service/canned-messages',
  '/support/tickets',
  '/support/mailboxes',
  '/support/sender-denylist',
  '/kb/bot-chats',
  '/kb/bot-conversations',
  '/system/notification-events',
  '/risk/login-history',
  '/price-monitor/tasks',
  '/charity/projects',
  '/charity/donations',
  '/charity/banners',
  '/charity/settings',
])

/** 前缀匹配的路径 → 对应需要检查权限的父路径 */
export const PREFIX_TO_PARENT: Record<string, string> = {
  '/user/detail/': '/user/list',
  // 设备关联页无独立菜单，权限复用全局登录流水页
  '/risk/devices/': '/risk/login-history',
  // 编辑器详情页复用列表页权限
  '/system/home-layouts/': '/system/home-layouts',
  // 黑名单命中流水页无独立菜单，权限复用规则列表页（后端同为 support.denylist.view）
  '/support/sender-denylist/': '/support/sender-denylist',
}

interface PermissionGuardProps {
  children: React.ReactNode
}

const ContentSpin = (
  <div className="flex items-center justify-center w-full h-full min-h-64">
    <Spin size="large" />
  </div>
)

/**
 * Per-route permission guard.
 * Must be rendered inside RouteGuard (authenticated context) and AppLayout.
 *
 * Logic:
 *  1. If the path is not in REGISTERED_PATHS → 404
 *  2. If the path is in PUBLIC_PATHS → allow
 *  3. If menus are still loading → show spinner (avoids false 403 on first load)
 *  4. If the path is not in the user's menu URLs → 403
 *  5. Otherwise → render children
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({ children }) => {
  const location = useLocation()
  const pathname = location.pathname.replace(/\/$/, '') || '/'

  const menuItems = useMenuStore((state) => state.items)
  const menuLoading = useMenuStore((state) => state.loading)

  // 1. Route doesn't exist at all → 404
  if (
    !REGISTERED_PATHS.has(pathname) &&
    !Object.keys(PREFIX_TO_PARENT).some((p) => pathname.startsWith(p))
  ) {
    return (
      <Suspense fallback={ContentSpin}>
        <NotFoundPage />
      </Suspense>
    )
  }

  // 2. Always-accessible paths
  if (PUBLIC_PATHS.has(pathname)) {
    return <>{children}</>
  }

  // 3. Menus not yet loaded — wait to avoid flashing 403
  if (menuLoading || menuItems.length === 0) {
    return ContentSpin
  }

  // 4. Check against the user's actual menu permissions
  // For detail pages, check the corresponding parent list page permission
  const matchedPrefix = Object.keys(PREFIX_TO_PARENT).find((p) => pathname.startsWith(p))
  const checkPath = matchedPrefix ? PREFIX_TO_PARENT[matchedPrefix] : pathname
  const allowedUrls = flattenMenuUrls(menuItems)
  if (!allowedUrls.has(checkPath)) {
    return (
      <Suspense fallback={ContentSpin}>
        <ForbiddenPage />
      </Suspense>
    )
  }

  // 5. Permitted
  return <>{children}</>
}

export default PermissionGuard
